import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { IOutreachEmailTemplate } from '@techspert-io/outreach-email';
import { MaterialModule } from '../../../../../shared/patterns/material-module';

type TemplateOption = Pick<
  IOutreachEmailTemplate,
  'templateId' | 'name' | 'favorite' | 'suggested'
> & {
  displayTags: string[];
  tagTooltipText: string;
};

@Component({
  selector: 'app-outreach-template-select',
  templateUrl: './outreach-template-select.component.html',
  styleUrls: ['./outreach-template-select.component.scss'],
  imports: [CommonModule, MaterialModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OutreachTemplateSelectComponent implements OnChanges {
  @Input() templates: IOutreachEmailTemplate[];
  @Input() group: FormGroup;

  private options: Record<'all' | 'suggested' | 'favorites', TemplateOption[]>;

  get outreachTemplateSelectForm(): FormGroup {
    return this.group || this.rootFormGroup.form;
  }

  get all() {
    return this.options.all;
  }

  get suggested() {
    return this.options.suggested;
  }

  get favorites() {
    return this.options.favorites;
  }

  constructor(private rootFormGroup: FormGroupDirective) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.templates) {
      this.setupTemplates();
    }
  }

  displayFn(templates: { templateId: string; name: string }[]) {
    return (val: string) =>
      templates?.find((t) => t.templateId === val)?.name || '';
  }

  private setupTemplates() {
    const all = this.templates.map(
      ({ templateId, name, suggested, favorite, tags }) => ({
        templateId,
        name,
        suggested,
        favorite,
        ...this.parseTags(tags),
      })
    );

    this.options = {
      all,
      suggested: all.filter((t) => t.suggested && !t.favorite),
      favorites: all.filter((t) => t.favorite),
    };
  }

  private parseTags(tags: string[]) {
    return {
      displayTags: [
        ...tags.slice(0, 2),
        ...(tags.length > 2 ? [`+${tags.length - 2}`] : []),
      ],
      tagTooltipText: tags.join(', '),
    };
  }
}
